import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Badge,
  CircularProgress,
  Dialog
} from '@mui/material';
import { CheckCircleOutline, HighlightOff, PersonOutline, StarBorder, NoPhotographyOutlined } from '@mui/icons-material';
import PillTabs from 'components/shared/tabs/PillTabs';
import AppBarContext from 'context/AppBarContext';
import {
  FLAGGED_PLAY_RESULTS,
  REVIEW_SELF_REPORTED_RESULT,
  REPORTED_ROUTINES,
  MODERATE_ROUTINE
} from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import { metricLabel, muscleLabel } from 'components/play/constants';
import GradeResultDialog from 'components/play/GradeResultDialog';

// El badge se dibuja fuera del flujo del texto: reserva espacio a la derecha solo cuando
// hay algo pendiente, para no descentrar la píldora cuando la cola está vacía.
const countBadge = (count, label) => (
  <Badge badgeContent={count} color="secondary">
    <Box sx={{ pr: count ? 2 : 0 }}>{label}</Box>
  </Badge>
);

/**
 * OTRIX Play (admin) — moderation. Two tabs: self-reported results flagged by sanity
 * caps (approve / reject), and shared routines reported by the community (approve /
 * reject-hide). Keeps the self-reported ranking and the forum clean.
 */
function PlayModerationPage() {
  const { t } = useTranslation(['play']);
  const { setAppBarTitle, setAppBarButton } = useContext(AppBarContext);
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [gradeTarget, setGradeTarget] = useState(null);

  useEffect(() => {
    setAppBarTitle(t('moderation.title', { ns: 'play' }));
    // Limpia el botón "Crear" heredado de la pantalla anterior (Ejercicios Play).
    setAppBarButton(null);
  }, [setAppBarTitle, setAppBarButton, t]);

  const resultsQuery = useQuery(FLAGGED_PLAY_RESULTS, { fetchPolicy: 'cache-and-network' });
  const routinesQuery = useQuery(REPORTED_ROUTINES, { fetchPolicy: 'cache-and-network' });

  const [review] = useMutation(REVIEW_SELF_REPORTED_RESULT, { refetchQueries: [{ query: FLAGGED_PLAY_RESULTS }] });
  const [moderate] = useMutation(MODERATE_ROUTINE, { refetchQueries: [{ query: REPORTED_ROUTINES }] });

  const results = resultsQuery.data?.flaggedPlayResults || [];
  const routines = routinesQuery.data?.reportedRoutines || [];

  const toast = (message, variant = 'success') =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `mod-${Date.now()}`, variant, message })));

  const handleReview = async (id, status) => {
    try {
      await review({ variables: { id, status } });
      toast(t(status === 'approved' ? 'moderation.approved' : 'moderation.rejected', { ns: 'play' }));
    } catch {
      toast(t('moderation.error', { ns: 'play' }), 'error');
    }
  };

  const handleModerate = async (id, status) => {
    try {
      await moderate({ variables: { id, status } });
      toast(t(status === 'approved' ? 'moderation.approved' : 'moderation.rejected', { ns: 'play' }));
    } catch {
      toast(t('moderation.error', { ns: 'play' }), 'error');
    }
  };

  const renderActions = (onApprove, onReject) => (
    <Stack direction="row" spacing={1}>
      <Button size="small" startIcon={<CheckCircleOutline />} variant="outlined" color="inherit" onClick={onApprove} sx={{ color: 'sessionStatus.completed.fg' }}>
        {t('moderation.approve', { ns: 'play' })}
      </Button>
      <Button size="small" startIcon={<HighlightOff />} variant="outlined" color="inherit" onClick={onReject} sx={{ color: 'sessionStatus.absent.fg' }}>
        {t('moderation.reject', { ns: 'play' })}
      </Button>
    </Stack>
  );

  const renderResults = () => {
    if (resultsQuery.loading && results.length === 0) {
      return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="secondary" /></Stack>;
    }
    if (results.length === 0) {
      return (
        <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" fontWeight={600}>{t('moderation.noResults', { ns: 'play' })}</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={1.5}>
        {results.map((r) => (
          <Stack
            key={r.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ p: 1.5, border: '1px solid', borderColor: 'hairline.main', borderRadius: 3 }}>
            {/* Los resultados derivados de un entrenamiento (source='workout') no traen foto. */}
            {r.photo_url ? (
              <Box
                component="img"
                src={r.photo_url}
                alt={r.exercise?.localized_name}
                onClick={() => setZoom(r.photo_url)}
                sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 2, cursor: 'zoom-in', flexShrink: 0 }}
              />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  flexShrink: 0,
                  border: '1px solid',
                  borderColor: 'hairline.main',
                  backgroundColor: 'surface.overlay',
                  color: 'text.secondary'
                }}>
                <NoPhotographyOutlined sx={{ fontSize: 22 }} />
              </Stack>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>{r.exercise?.localized_name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {r.user?.full_name} · {r.value} {metricLabel(t, r.metric)}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip size="small" label={t('moderation.flagged', { ns: 'play' })} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
                {r.rating ? (
                  <Chip size="small" icon={<StarBorder sx={{ fontSize: 14 }} />} label={`${r.rating}/5`} sx={{ bgcolor: 'surface.raised', color: 'gold.main' }} />
                ) : null}
              </Stack>
            </Box>
            <Stack spacing={1}>
              {renderActions(() => handleReview(r.id, 'approved'), () => handleReview(r.id, 'rejected'))}
              <Button size="small" startIcon={<StarBorder />} variant="outlined" color="inherit" onClick={() => setGradeTarget(r)} sx={{ color: 'gold.main', borderColor: 'gold.main' }}>
                {r.rating ? t('moderation.regrade', { ns: 'play', defaultValue: 'Editar calificación' }) : t('moderation.grade', { ns: 'play', defaultValue: 'Calificar' })}
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  };

  const renderRoutines = () => {
    if (routinesQuery.loading && routines.length === 0) {
      return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="secondary" /></Stack>;
    }
    if (routines.length === 0) {
      return (
        <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" fontWeight={600}>{t('moderation.noRoutines', { ns: 'play' })}</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={1.5}>
        {routines.map((r) => (
          <Stack
            key={r.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ p: 1.5, border: '1px solid', borderColor: 'hairline.main', borderRadius: 3 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>{r.name}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                <PersonOutline sx={{ fontSize: 15 }} />
                <Typography variant="caption">{r.author_name}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {r.muscle_focus && <Chip size="small" variant="outlined" label={muscleLabel(t, r.muscle_focus)} />}
                <Chip size="small" label={t('moderation.reports', { ns: 'play', count: r.report_count })} sx={{ bgcolor: 'sessionStatus.absent.bg', color: 'sessionStatus.absent.fg' }} />
                <Chip size="small" variant="outlined" label={r.moderation_status} />
              </Stack>
            </Box>
            {renderActions(() => handleModerate(r.id, 'approved'), () => handleModerate(r.id, 'rejected'))}
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PillTabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        ariaLabel={t('moderation.title', { ns: 'play' })}
        sx={{ mb: 3 }}
        tabs={[
          { key: 'results', label: countBadge(results.length, t('moderation.resultsTab', { ns: 'play' })) },
          { key: 'routines', label: countBadge(routines.length, t('moderation.routinesTab', { ns: 'play' })) }
        ]}
      />

      {tab === 0 ? renderResults() : renderRoutines()}

      <Dialog open={Boolean(zoom)} onClose={() => setZoom(null)} maxWidth="md">
        {zoom && <Box component="img" src={zoom} alt="" sx={{ maxWidth: '100%', display: 'block' }} />}
      </Dialog>

      <GradeResultDialog
        open={Boolean(gradeTarget)}
        result={gradeTarget}
        refetchQueries={[{ query: FLAGGED_PLAY_RESULTS }]}
        onClose={() => setGradeTarget(null)}
      />
    </Box>
  );
}

export default PlayModerationPage;
